import { useCallback, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import type { ActivityListItem } from "@/components/Molecules/ActivityList/ActivityList";
import { ActivityList } from "@/components/Molecules/ActivityList/ActivityList";
import { EmptyState } from "@/components/Molecules/EmptyState/EmptyState";
import {
	type AppTranslateFn,
	useAppTranslation,
} from "@/hooks/use-app-translation";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import { formatLongDate } from "@/utils/utils";

/** Fields the Activity list reads from an entity-type audit log. */
export interface EntityTypeActivityLog {
	id: string;
	action?: string | null;
	date?: string | null;
	fieldName?: string | null;
	oldValue?: string | null;
	newValue?: string | null;
	entityType?: string | null;
	userId?: string | null;
	user?: {
		id?: string | null;
		hasAvatar?: boolean;
		displayName?: string | null;
		email?: string | null;
		firstName?: string | null;
		lastName?: string | null;
	} | null;
}

export interface EntityTypeActivityListProps {
	pages: EntityTypeActivityLog[][] | undefined;
	isLoading: boolean;
	entityLabel: string;
	emptyTitle: string;
	emptyDescription: string;
	ariaLabel: string;
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	fetchNextPage: () => void;
}

/**
 * Entity-type Activity tab body — Figma ActivityList (drawer Activity 28:58426).
 * The audit query stays with the caller. This list only presents those logs.
 */
export function EntityTypeActivityList({
	pages,
	isLoading,
	entityLabel,
	emptyTitle,
	emptyDescription,
	ariaLabel,
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
}: EntityTypeActivityListProps) {
	const { t } = useAppTranslation();
	const fromLabel = t("main.history.from");
	const toLabel = t("main.history.to");
	const fieldDisplayNames = useMemo(
		() => ({
			SubOrganization: t("subOrganization.entityLabel"),
		}),
		[t],
	);

	const logs = useMemo(() => {
		return [...(pages ?? []).flat()].sort((a, b) => {
			if (!a.date) return 1;
			if (!b.date) return -1;
			return new Date(b.date).getTime() - new Date(a.date).getTime();
		});
	}, [pages]);
	const actors = useMemo(() => {
		const byId = new Map<string, boolean>();
		for (const log of logs) {
			const actorId = log.user?.id ?? log.userId;
			if (!actorId) continue;
			const actorHasAvatar = log.user?.hasAvatar === true;
			if (byId.get(actorId)) continue;
			if (actorHasAvatar) byId.set(actorId, true);
			else if (!byId.has(actorId)) byId.set(actorId, false);
		}
		return [...byId];
	}, [logs]);
	const [avatarSrcByUserId, setAvatarSrcByUserId] = useState<
		Record<string, string | null>
	>({});
	const setActorAvatarSrc = useCallback(
		(actorId: string, src: string | null) => {
			setAvatarSrcByUserId((current) => {
				if (current[actorId] === src) return current;
				return { ...current, [actorId]: src };
			});
		},
		[],
	);

	const items: ActivityListItem[] = useMemo(() => {
		return logs.map((log, index) => {
			const displayName = getUserDisplayName(log.user);
			const dateLabel = log.date ? formatLongDate(log.date) : "";
			const secondaryText = [displayName, dateLabel]
				.filter(Boolean)
				.join(" • ");
			const actorId = log.user?.id ?? log.userId ?? undefined;
			const avatarSrc = actorId ? avatarSrcByUserId[actorId] : null;

			return {
				id: log.id,
				primaryText: getEventTitle(log, entityLabel, fieldDisplayNames, t),
				secondaryText,
				avatarAlt: displayName || undefined,
				avatarSrc: avatarSrc || undefined,
				avatarInitials: avatarSrc ? undefined : getUserInitials(log.user),
				defaultExpanded: index === 0,
				content: (
					<ActivityEventContent
						log={log}
						entityLabel={entityLabel}
						fieldDisplayNames={fieldDisplayNames}
						fromLabel={fromLabel}
						toLabel={toLabel}
					/>
				),
			};
		});
	}, [
		avatarSrcByUserId,
		logs,
		entityLabel,
		fieldDisplayNames,
		fromLabel,
		toLabel,
		t,
	]);

	const { ref: loadMoreRef, inView } = useInView({
		threshold: 0,
		rootMargin: "0px 0px 300px 0px",
	});

	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	if (isLoading) {
		return (
			<div className="flex w-full min-h-[280px] flex-col items-center justify-center py-8">
				<LoadingIndicator type="line-simple" size="lg" supportingText />
			</div>
		);
	}

	if (items.length === 0) {
		return (
			<EmptyState
				type="default"
				title={emptyTitle}
				description={emptyDescription}
				showAction={false}
				className="min-h-[280px] max-w-none py-8"
			/>
		);
	}

	return (
		<div className="flex w-full flex-col gap-2">
			{actors.map(([actorId, actorHasAvatar]) => (
				<ActivityActorAvatar
					key={actorId}
					userId={actorId}
					hasAvatar={actorHasAvatar}
					onSrc={setActorAvatarSrc}
				/>
			))}
			<ActivityList items={items} aria-label={ariaLabel} />
			{(hasNextPage || isFetchingNextPage) && (
				<div
					ref={loadMoreRef}
					className="flex items-center justify-center py-3"
				>
					{isFetchingNextPage ? (
						<LoadingIndicator type="line-simple" size="sm" />
					) : null}
				</div>
			)}
		</div>
	);
}

function ActivityActorAvatar({
	userId,
	hasAvatar,
	onSrc,
}: {
	userId: string;
	hasAvatar: boolean;
	onSrc: (userId: string, src: string | null) => void;
}) {
	const { src } = useUserAvatarSrc(userId, hasAvatar);
	useEffect(() => {
		onSrc(userId, src);
		return () => onSrc(userId, null);
	}, [onSrc, src, userId]);
	return null;
}

function ActivityEventContent({
	log,
	entityLabel,
	fieldDisplayNames,
	fromLabel,
	toLabel,
}: {
	log: EntityTypeActivityLog;
	entityLabel: string;
	fieldDisplayNames: Record<string, string>;
	fromLabel: string;
	toLabel: string;
}) {
	const { t } = useAppTranslation();
	const fieldLabel = getFieldDisplayName(log.fieldName, fieldDisplayNames);
	const hasOld = log.oldValue != null && log.oldValue !== "";
	const hasNew = log.newValue != null && log.newValue !== "";
	const action = (log.action ?? "").toLowerCase();

	if (!hasOld && !hasNew) {
		if (action === "create") {
			return (
				<p className="text-sm text-secondary">
					{t("common.activity.entityWasCreated", { entity: entityLabel })}
				</p>
			);
		}
		if (action === "delete") {
			return (
				<p className="text-sm text-secondary">
					{t("common.activity.entityWasDeleted", { entity: entityLabel })}
				</p>
			);
		}
		return (
			<p className="text-sm text-secondary">
				{getEventTitle(log, entityLabel, fieldDisplayNames, t)}
			</p>
		);
	}

	return (
		<div className="flex flex-col gap-2 text-sm">
			{log.fieldName ? (
				<p className="font-semibold text-primary capitalize">{fieldLabel}</p>
			) : null}
			<div className="flex flex-wrap items-center gap-1.5 text-secondary">
				{hasOld ? (
					<>
						<span className="text-tertiary">{fromLabel}</span>
						<span>"{log.oldValue}"</span>
					</>
				) : null}
				{hasNew ? (
					<>
						<span className="text-tertiary">{toLabel}</span>
						<span>"{log.newValue}"</span>
					</>
				) : null}
			</div>
		</div>
	);
}

function getEventTitle(
	log: EntityTypeActivityLog,
	entityLabel: string,
	fieldDisplayNames: Record<string, string>,
	t: AppTranslateFn,
): string {
	const action = (log.action ?? "").toLowerCase();
	const entity = entityLabel || formatFieldName(log.entityType);
	const field = capitalize(getFieldDisplayName(log.fieldName, fieldDisplayNames));

	if (action === "create") {
		return t("common.activity.createdEntity", { entity });
	}
	if (action === "delete") {
		return t("common.activity.deletedEntity", { entity });
	}
	if (action === "update") {
		if (log.fieldName) {
			return t("common.activity.updatedField", { field });
		}
		return t("common.activity.updatedEntity", { entity });
	}

	if (log.action) return capitalize(log.action);
	return t("common.activity.event");
}

function getUserDisplayName(user: EntityTypeActivityLog["user"]): string {
	if (!user) return "";
	if (user.firstName && user.lastName) {
		return `${user.firstName} ${user.lastName}`;
	}
	if (user.displayName) return user.displayName;
	if (user.email) return user.email;
	return "";
}

function getUserInitials(
	user: EntityTypeActivityLog["user"],
): string | undefined {
	if (!user) return undefined;
	const firstName = user.firstName || "";
	const lastName = user.lastName || "";
	if (firstName && lastName) {
		return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
	}
	if (user.displayName) {
		const parts = user.displayName.trim().split(/\s+/);
		if (parts.length >= 2) {
			return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
		}
		return user.displayName.substring(0, 2).toUpperCase();
	}
	if (user.email) return user.email.substring(0, 2).toUpperCase();
	return undefined;
}

function formatFieldName(fieldName: string | null | undefined): string {
	if (!fieldName) return "";
	return fieldName
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
		.toLowerCase();
}

function getFieldDisplayName(
	fieldName: string | null | undefined,
	fieldDisplayNames: Record<string, string>,
): string {
	if (!fieldName) return "";
	const fromApi = fieldDisplayNames[fieldName];
	if (fromApi) return fromApi.toLowerCase();
	return formatFieldName(fieldName);
}

function capitalize(value: string): string {
	if (!value) return value;
	return value.charAt(0).toUpperCase() + value.slice(1);
}
